import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { canNameServerOf } from '../lib/fleet-rename';
import type { UtilBox } from '../lib/fleetUtilization';
import { createServerNameWrite } from '../lib/name-write';
import { ServerNameForm } from './ServerNameForm';
import { Button } from './ui/Button';

// The details panel's About "Server" row with its edit (#6566, owner ruling G7, not in the
// prototype): the server's name, and for an admin on this org's own machine (G21, G23) a
// pencil that opens the server-name form. The write names every own-org machine the
// UNFILTERED read places on this machine's server (`serverLabelBody`), so another org's
// machines on the same address are left out and keep "Unnamed server, <address>".

export function MachineServerName(props: {
  readonly box: UtilBox;
  /** What the row reads (`serverText`): the label, or "Unnamed server, <address>". */
  readonly text: string;
  /** The UNFILTERED roster. */
  readonly allBoxes: readonly UtilBox[];
  readonly canWrite: boolean;
  readonly token: string | null;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  readonly onWritten: () => void;
}): JSX.Element {
  const [editing, setEditing] = createSignal(false);
  const write = createServerNameWrite({
    all: () => props.allBoxes,
    groupKey: () => props.box.server.key,
    token: props.token,
    notify: (m, tone) => props.notify(m, tone),
    onWritten: () => props.onWritten(),
    onDone: () => setEditing(false),
  });
  const offered = (): boolean => canNameServerOf(props.box, props.allBoxes, props.canWrite);
  return (
    <>
      <span class="facts__num">{props.text}</span>
      <Show when={offered()}>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('fleet.machines.server_name_edit')}
          aria-expanded={editing()}
          onClick={() => {
            write.reset();
            setEditing((now) => !now);
          }}
        >
          <span aria-hidden="true">✎</span>
        </Button>
      </Show>
      <Show when={editing() && offered()}>
        <ServerNameForm
          id="machine-panel-server-name"
          currentLabel={props.box.server.label}
          pending={write.pending()}
          error={write.error()}
          onSave={write.save}
          onCancel={() => setEditing(false)}
        />
      </Show>
    </>
  );
}
